import { isEqual } from 'lodash'
import { useMemo, useState, useEffect, useRef } from 'react'

export enum SelectionMode {
  SINGLE = 'single',
  MULTIPLE = 'multiple',
  GROUP_PRIORITY = 'group_priority',
  RAW = 'raw',
}

interface UseSelectionOptions {
  mode: SelectionMode
  initialSelectedIds: string[]
  allIds: string[]
  getChildrenIds?: (id: string) => string[]
  onSelectionChange?: (selectedIds: string[]) => void
}

const useSelection = ({
  mode,
  initialSelectedIds = [],
  allIds = [],
  getChildrenIds,
  onSelectionChange,
}: UseSelectionOptions) => {
  const [selectedIds, setSelectedIds] = useState<string[]>(initialSelectedIds)
  const prevSelectedIdsRef = useRef<string[]>(initialSelectedIds)

  useEffect(() => {
    if (onSelectionChange && !isEqual(selectedIds, prevSelectedIdsRef.current)) {
      prevSelectedIdsRef.current = selectedIds
      onSelectionChange(selectedIds)
    }
  }, [selectedIds, onSelectionChange])

  useEffect(() => {
    setSelectedIds(initialSelectedIds)
  }, [initialSelectedIds])

  const collectAllChildrenIds = (ids: string[]): string[] => {
    const collected = new Set<string>()

    const collect = (id: string) => {
      if (!collected.has(id)) {
        collected.add(id)
        const children = getChildrenIds?.(id) || []
        children.forEach(collect)
      }
    }

    ids.forEach(collect)
    return Array.from(collected)
  }

  const isAllSelected = useMemo(() => selectedIds.length === allIds.length && allIds.length > 0, [selectedIds, allIds])

  const resetSelection = (ids: string[] = []) => {
    setSelectedIds(ids)
  }

  const toggleSelection = (id: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev)

      if (mode === SelectionMode.SINGLE) {
        return prev.includes(id) ? [] : [id]
      }

      if (mode === SelectionMode.MULTIPLE) {
        const index = prev.indexOf(id)

        if (getChildrenIds) {
          const allRelatedIds = collectAllChildrenIds([id])

          if (index !== -1) {
            allRelatedIds.forEach((relatedId) => {
              next.delete(relatedId)
            })
          } else {
            allRelatedIds.forEach((relatedId) => {
              next.add(relatedId)
            })
          }
        } else {
          if (index !== -1) {
            next.delete(id)
          } else {
            next.add(id)
          }
        }

        return Array.from(next)
      }

      if (mode === SelectionMode.GROUP_PRIORITY) {
        setSelectedIds((prev) => {
          const next = new Set(prev)
          const isSelected = next.has(id)
          const childrenIds = getChildrenIds?.(id) ?? []
          const isLeaf = childrenIds.length === 0

          if (isSelected) {
            next.delete(id)
            return Array.from(next)
          }

          const removeAllAncestors = (childId: string) => {
            for (const maybeParent of allIds) {
              const children = getChildrenIds?.(maybeParent) ?? []
              if (children.includes(childId)) {
                if (next.has(maybeParent)) {
                  next.delete(maybeParent)
                }
                removeAllAncestors(maybeParent)
              }
            }
          }

          if (isLeaf) {
            removeAllAncestors(id)
            next.add(id)
          } else {
            const allChildIds = collectAllChildrenIds([id])
            allChildIds.forEach((childId) => {
              if (next.has(childId)) {
                next.delete(childId)
              }
            })

            removeAllAncestors(id)

            next.add(id)
          }

          return Array.from(next)
        })
      }

      if (mode === SelectionMode.RAW) {
        const index = prev.indexOf(id)
        if (index !== -1) {
          next.delete(id)
        } else {
          next.add(id)
        }
        return Array.from(next)
      }

      return prev
    })
  }

  const clearSelection = () => setSelectedIds([])

  const selectAll = () => {
    if (mode === SelectionMode.MULTIPLE && allIds?.length) {
      setSelectedIds(allIds)
    }
  }

  return {
    toggleSelection,
    isSelected: (id: string) => selectedIds.includes(id),
    collectAllChildrenIds,
    selectedIds,
    clearSelection,
    selectAll,
    resetSelection,
    isAllSelected,
  }
}

export default useSelection
